import { type SystemNotification, notificationSourceMeta, severityMeta } from '@/mocks/saGlobalNotificationCenter';

interface NotificationDetailModalProps {
  notification: SystemNotification;
  onClose: () => void;
  onMarkRead: (notification: SystemNotification) => void;
  onToggleImportant: (notification: SystemNotification) => void;
  onArchive: (notification: SystemNotification) => void;
  onDelete: (notification: SystemNotification) => void;
}

const SOURCE_FALLBACK = { label: 'Desconocido', icon: 'ri-question-line', color: 'text-foreground-400' };
const SEVERITY_FALLBACK = { label: '--', icon: 'ri-question-line', bg: 'bg-background-300', textColor: 'text-foreground-400', badgeBg: 'bg-background-100' };

function fullDateTime(dateStr: string): string {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '--';
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '--';
  }
}

export default function NotificationDetailModal({
  notification,
  onClose,
  onMarkRead,
  onToggleImportant,
  onArchive,
  onDelete,
}: NotificationDetailModalProps) {
  const sourceMeta = notificationSourceMeta[notification.source] || SOURCE_FALLBACK;
  const sevMeta = severityMeta[notification.severity] || SEVERITY_FALLBACK;
  const isArchived = notification.archived;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${sevMeta.bg} shrink-0`}></div>
              <div className={`w-9 h-9 rounded-xl ${isArchived || notification.read ? 'bg-background-200' : 'bg-amber-100'} flex items-center justify-center`}>
                <i className={`${sourceMeta.icon} text-sm ${isArchived || notification.read ? 'text-foreground-400' : sourceMeta.color}`}></i>
              </div>
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm truncate">{notification.title || 'Sin título'}</h3>
              <p className="text-foreground-400 text-[10px]">{fullDateTime(notification.createdAt)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0 ml-2"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold bg-background-100 text-foreground-600">
              {sourceMeta.label}
            </span>
            <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold ${sevMeta.badgeBg} ${sevMeta.textColor}`}>
              <i className={`${sevMeta.icon} text-[9px] mr-1`}></i>
              {sevMeta.label}
            </span>
            {notification.important && (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-700">
                <i className="ri-star-fill text-[9px]"></i>
                Importante
              </span>
            )}
            {isArchived && (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-background-100 text-foreground-400">
                <i className="ri-archive-line text-[9px]"></i>
                Archivada
              </span>
            )}
          </div>

          {/* Message */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-wrap">{notification.message || 'Sin mensaje'}</p>
          </div>

          {/* Metadata */}
          {(notification.read !== undefined || notification.metadata) && (
            <div className="bg-background-100 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-foreground-400">Estado</span>
                <span className={notification.read ? 'text-foreground-500' : 'text-amber-600 font-semibold'}>
                  {isArchived ? 'Archivada' : notification.read ? 'Leída' : 'No leída'}
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-foreground-400">ID</span>
                <span className="text-foreground-500 font-mono text-[10px]">#SA-NTF-{String(notification.id || 0).padStart(4, '0')}</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-foreground-400">Recibida</span>
                <span className="text-foreground-600">{fullDateTime(notification.createdAt)}</span>
              </div>
              {notification.metadata?.affectedRadio && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Radio afectada</span>
                  <span className="text-foreground-600 font-medium">{notification.metadata.affectedRadio}</span>
                </div>
              )}
              {notification.metadata?.affectedUser && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Usuario afectado</span>
                  <span className="text-foreground-600 font-medium">{notification.metadata.affectedUser}</span>
                </div>
              )}
              {notification.metadata?.errorCode && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Código de error</span>
                  <span className="text-foreground-500 font-mono text-[10px]">{notification.metadata.errorCode}</span>
                </div>
              )}
              {notification.metadata?.transactionId && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Transacción</span>
                  <span className="text-foreground-500 font-mono text-[10px]">{notification.metadata.transactionId}</span>
                </div>
              )}
              {notification.metadata?.planName && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Plan</span>
                  <span className="text-foreground-600 font-medium">{notification.metadata.planName}</span>
                </div>
              )}
              {notification.metadata?.storageUsed && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Almacenamiento</span>
                  <span className="text-foreground-600 font-medium">{notification.metadata.storageUsed}</span>
                </div>
              )}
              {notification.metadata?.aiModel && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Modelo IA</span>
                  <span className="text-foreground-600 font-medium">{notification.metadata.aiModel}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-background-200 shrink-0 gap-2">
          <div className="flex items-center gap-2">
            {notification.actionLabel && notification.actionPath && !isArchived && (
              <a
                href={notification.actionPath}
                onClick={onClose}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line text-sm"></i>
                {notification.actionLabel}
              </a>
            )}
            {!notification.read && !isArchived && (
              <button
                onClick={() => { onMarkRead(notification); onClose(); }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-amber-50 text-amber-600 text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line text-sm"></i>
                Marcar leída
              </button>
            )}
            <button
              onClick={() => { onToggleImportant(notification); }}
              className={`inline-flex items-center gap-1 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                notification.important
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-background-100 text-foreground-500 hover:bg-amber-50 hover:text-amber-600'
              }`}
            >
              <i className={`${notification.important ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
              {notification.important ? 'Importante' : 'Destacar'}
            </button>
            {!isArchived ? (
              <button
                onClick={() => { onArchive(notification); onClose(); }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-amber-50 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-archive-line text-sm"></i>
                Archivar
              </button>
            ) : (
              <button
                onClick={() => { onArchive(notification); onClose(); }}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-inbox-unarchive-line text-sm"></i>
                Restaurar
              </button>
            )}
          </div>
          <button
            onClick={() => { onDelete(notification); onClose(); }}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-delete-bin-line text-sm"></i>
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}